import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import { FC, createRef } from "react";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  OptionsDataType,
  RowProps,
  RowValue,
} from "../../../../../../../../../components";
import MuiChipWithPopover from "../../../../../../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../../../../../../components/statusBadge/StatusBadge";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../../../../../helpers/constants";
import {
  TableActionType,
  TableActions,
} from "../../../../../../../../../helpers/types/common";
import {
  buildActionMenuItems,
  isEmpty,
} from "../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../../../hooks/useSiteCore";
import {
  ProjectActivitiesFormKeys,
  ProjectActivitiesTabProps,
} from "../modelsAndSchemas/models";
import { activitiesListSiteCoreKeys } from "../siteCore/SiteCoreKeys";

export const pageId = "Create-Client-Activities-tab";

export const ProjectActivitiesTab: FC<ProjectActivitiesTabProps> = ({
  rows,
  control,
  tableRef,
  isView,
  uniqueFilters,
  filteredResults,
  onProjectActivityViewClick,
  deleteActivityOrDeliverableAction,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const onClickAction = (action: TableActionType, selectedRow: any) => {
    if (action === TableActions.edit) {
      onProjectActivityViewClick(selectedRow, true);
    } else {
      deleteActivityOrDeliverableAction(selectedRow);
    }
  };
  const renderFormControl = (
    control: any,
    name: keyof typeof ProjectActivitiesFormKeys,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={options}
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={ProjectActivitiesFormKeys.topic}
      key={ProjectActivitiesFormKeys.topic}
    >
      <MuiTextField
        id={`${pageId}-${ProjectActivitiesFormKeys.topic}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      control,
      ProjectActivitiesFormKeys.activityType,
      uniqueFilters.activityType,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectActivitiesFormKeys.activityType}`,
    ),
    renderFormControl(
      control,
      ProjectActivitiesFormKeys.modality,
      uniqueFilters.modality,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectActivitiesFormKeys.modality}`,
    ),

    renderFormControl(
      control,
      ProjectActivitiesFormKeys.projectFramework,
      uniqueFilters.projectFramework,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectActivitiesFormKeys.projectFramework}`,
    ),
    renderFormControl(
      control,
      ProjectActivitiesFormKeys.activityStatus,
      fetchDropdownValues(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.statusList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectActivitiesFormKeys.activityStatus}`,
    ),
    renderFormControl(
      control,
      ProjectActivitiesFormKeys.units,
      uniqueFilters.units,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectActivitiesFormKeys.units}`,
    ),
    <></>,

    <></>,
  ];

  const columns = [
    {
      id: ProjectActivitiesFormKeys.topic,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.topic,
      ),
      isSortVisible: true,
      className: "cell-topics",
      render: (row: any) => (
        <MuiLink onClick={() => onProjectActivityViewClick(row)}>
          <MuiBox>
            <MuiEllipseLabel
              labelTypoClassName="Link"
              text={row?.topicName}
              labelTypoVariant="h3"
              placement="bottom-start"
              pointerCursor
            />
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: ProjectActivitiesFormKeys.activityType,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.activityType,
      ),

      isSortVisible: true,
      className: "cell-activity-type",
    },
    {
      id: ProjectActivitiesFormKeys.modality,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.modality,
      ),
      isSortVisible: true,
      className: "cell-modality",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.modality?.map((item: string) => ({
            label: item,
            value: item,
          }))}
          id={"selected-modality"}
          isEditable={false}
          headerLabel={fetchLabel(
            activitiesListSiteCoreKeys.activitiesList,
            activitiesListSiteCoreKeys.modality,
          )}
          isDialogTitleWithEllipsis
          gridRef={createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },

    {
      id: ProjectActivitiesFormKeys.projectFramework,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.projectFramework,
      ),
      isSortVisible: true,
    },
    {
      id: ProjectActivitiesFormKeys.activityStatus,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.activityStatus,
      ),
      isSortVisible: true,
      className: "cell-activity-status",
      render: (row: any) => {
        const activityStatusLabel = fetchValueByKeyOfDropdown(
          activitiesListSiteCoreKeys.activitiesList,
          activitiesListSiteCoreKeys.statusList,
          row?.activityStatus,
        );
        return (
          <StatusBadge
            status={row.activityStatus}
            label={activityStatusLabel}
          />
        );
      },
    },

    {
      id: ProjectActivitiesFormKeys.units,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.units,
      ),
      isSortVisible: true,
      className: "cell-units",
    },
    {
      id: ProjectActivitiesFormKeys.measurement,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.measurement,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },

    {
      id: "action",
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.action,
      ),
      render: (selectedRow: any) => {
        const menu = [
          {
            permissionKey:
              MODULE_PERMISSIONS.PROJECT_EDIT_ACTIVITIES_AND_DELIVERABLES,
            create: () => ({
              label: commonLabels.edit,
              dataTestId: "editActivity",
              onClick: () => onClickAction(TableActions.edit, selectedRow),
            }),
          },
        ];
        if (isEmpty(selectedRow?.projectFramework)) {
          menu.push({
            permissionKey:
              MODULE_PERMISSIONS.PROJECT_DELETE_ACTIVITIES_AND_DELIVERABLES,

            create: () => ({
              label: commonLabels.delete,
              dataTestId: "deleteActivity",
              onClick: () => onClickAction(TableActions.delete, selectedRow),
            }),
          });
        }

        const menuItems = buildActionMenuItems(
          selectedRow,
          menu,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  return (
    <MuiGrid container data-testid="search-activities">
      <MuiGrid size={{ xs: 12 }} className="header-right">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={
            isView ? tableHeaderForm.slice(0, -1) : tableHeaderForm
          }
          columns={isView ? columns.slice(0, -1) : columns}
          tableResults={
            [...filteredResults] as unknown as Record<string, RowValue>[]
          }
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={fetchLabel(
            activitiesListSiteCoreKeys.activitiesList,
            activitiesListSiteCoreKeys.activitiesNotAvailable,
          )}
          priorityResults={[]}
          isLastColumnSticky={!isView}
          isShowDashIfValueEmpty
        />
      </MuiGrid>
    </MuiGrid>
  );
};
